class ProductItem extends HTMLElement {
  constructor() {
    super();

    this._activeImageIndex = 0;
    this.images = [];
    this.isHovered = false;
    this.isMobile = false;
    this.pageScrollHandler = null;
  }

  isMobileView() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  connectedCallback() {
    this.init();
    this._resizeHandler = () => {
      const wasMobile = this.isMobile;
      const isNowMobile = this.isMobileView();

      if (wasMobile !== isNowMobile) {
        this.isMobile = isNowMobile;
        this.init();
      }
    };

    window.addEventListener('resize', this._resizeHandler);
  }

  disconnectedCallback() {
    if (this.pageScrollHandler) {
      window.removeEventListener('scroll', this.pageScrollHandler);
    }
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
  }

  init() {
    this.images = Array.from(this.querySelectorAll('img.ProductItem__Image'));

    if (this.images.length === 0) {
      this.images = Array.from(this.querySelectorAll('img[data-media-id]'));
    }

    this._activeImageIndex = 0;
    this.isMobile = this.isMobileView();

    if (this.isMobile) {
      this.setupMobile();
    } else {
      this.setupDesktop();
    }
  }

  setupDesktop() {
    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );

    if (carouselImages.length > 0) {
      this.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
      this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
      this.createArrows();
    }
  }

  setupMobile() {
    if (this.images.length <= 1) return;

    const imageContainer = this.querySelector('.ProductItem__ImageContainer');
    const aspectRatio =
      imageContainer?.querySelector('.AspectRatio') || imageContainer;

    if (!aspectRatio) return;

    // Create scroll wrapper
    const scrollWrapper = document.createElement('div');

    scrollWrapper.className = 'ProductItem__ScrollWrapper';

    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );
    const alternateImage = this.images.find((img) =>
      img.classList.contains('ProductItem__Image--alternate'),
    );
    const mainImage = carouselImages[0];

    // Always main, then first unique alternate by URL, then all other unique images
    const orderedImages = [];
    const addedUrls = new Set();

    if (mainImage) {
      orderedImages.push(mainImage);

      const mainUrl = mainImage.getAttribute('data-src');

      if (mainUrl) addedUrls.add(mainUrl);
    }

    // Add first unique alternate image by URL (not main)
    let alternateUrl = null;

    if (alternateImage) {
      alternateUrl = alternateImage.getAttribute('data-src');

      if (alternateUrl && !addedUrls.has(alternateUrl)) {
        const firstAlt = carouselImages.find(
          (img) =>
            img.getAttribute('data-src') === alternateUrl && img !== mainImage,
        );

        if (firstAlt) {
          orderedImages.push(firstAlt);
          addedUrls.add(alternateUrl);
        }
      }
    }

    // Add all other unique images (skip main and alternate by URL)
    carouselImages.forEach((img) => {
      const imgUrl = img.getAttribute('data-src');

      if (!imgUrl || addedUrls.has(imgUrl)) return;

      orderedImages.push(img);
      addedUrls.add(imgUrl);
    });

    orderedImages.forEach((img) => {
      const slide = document.createElement('div');
      slide.className = 'ProductItem__ImageSlide';

      const clonedImg = img.cloneNode(true);
      clonedImg.className = 'ProductItem__Image Image--fadeIn lazyautosizes';

      this.fixImageUrls(clonedImg);

      const dataSrc = clonedImg.getAttribute('data-src');

      if (dataSrc) {
        clonedImg.src = dataSrc;
      }

      slide.appendChild(clonedImg);
      scrollWrapper.appendChild(slide);
    });

    this.images.forEach((img) => img.remove());
    aspectRatio.appendChild(scrollWrapper);

    this.images = Array.from(scrollWrapper.querySelectorAll('img'));
    this.createProgressBarAfterImageWrapper();

    scrollWrapper.addEventListener(
      'scroll',
      this.handleMobileScroll.bind(this),
    );

    this.setupPageScrollReset();

    if (window.lazySizes) {
      window.lazySizes.autoSizer.updateElems(this.images);
    }
  }

  fixImageUrls(img) {
    ['data-src', 'src', 'data-srcset', 'srcset'].forEach((attr) => {
      const value = img.getAttribute(attr);

      if (value && value.includes('{width}')) {
        img.setAttribute(attr, value.replace(/{width}x/g, '600x'));
      }
    });
  }

  createArrows() {
    const container = this.querySelector('.ProductItem__ImageContainer');

    if (!container) return;

    const prevArrow = document.createElement('button');

    prevArrow.className = 'ProductItem__PrevArrow';
    prevArrow.style.opacity = '0';
    prevArrow.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="6" height="11" viewBox="0 0 6 11" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M5.86133 1.57653L1.6764 5.4999L5.86133 9.42327L5.14139 10.1912L0.137332 5.4999L5.14139 0.808594L5.86133 1.57653Z" fill="black"/>
      </svg>
    `;
    prevArrow.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.changeImage(-1);
    };

    const nextArrow = document.createElement('button');
    nextArrow.className = 'ProductItem__NextArrow';
    nextArrow.style.opacity = '0';
    nextArrow.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="6" height="11" viewBox="0 0 6 11" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.138672 9.42347L4.3236 5.5001L0.138671 1.57673L0.85861 0.808798L5.86267 5.5001L0.85861 10.1914L0.138672 9.42347Z" fill="black"/>
      </svg>
    `;
    nextArrow.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.changeImage(1);
    };

    container.appendChild(prevArrow);
    container.appendChild(nextArrow);
  }

  getRolloverImageIndex() {
    const alternateImage = this.images.find((img) =>
      img.classList.contains('ProductItem__Image--alternate'),
    );

    if (alternateImage) {
      const carouselImages = this.images.filter((img) =>
        img.classList.contains('ProductItem__Image--carousel'),
      );
      const alternateSrc = alternateImage.getAttribute('data-src');

      if (alternateSrc) {
        const matchBySrc = carouselImages.find(
          (img) => img.getAttribute('data-src') === alternateSrc,
        );

        if (matchBySrc) {
          return this.images.indexOf(matchBySrc);
        }
      }

      const alternateSrcset = alternateImage.getAttribute('data-srcset');

      if (alternateSrcset) {
        const matchBySrcset = carouselImages.find(
          (img) => img.getAttribute('data-srcset') === alternateSrcset,
        );

        if (matchBySrcset) {
          return this.images.indexOf(matchBySrcset);
        }
      }
    }

    // Fallback: first carousel image or 0
    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );

    return carouselImages.length > 0
      ? this.images.indexOf(carouselImages[0])
      : 0;
  }

  handleMouseEnter() {
    this.isHovered = true;
    this._activeImageIndex = 0;
    this.updateCarousel();
    this.showArrows();
  }

  handleMouseLeave() {
    this.isHovered = false;
    this._activeImageIndex = 0;
    this.updateCarousel();
    this.hideArrows();
  }

  changeImage(direction) {
    const carouselImages = this.images.filter((img) =>
      img.classList.contains('ProductItem__Image--carousel'),
    );

    if (carouselImages.length === 0) return;

    // If this is the first arrow click (active index is 0), start from rollover image
    if (this._activeImageIndex === 0) {
      const rolloverIndex = this.getRolloverImageIndex();

      this._activeImageIndex = rolloverIndex;
    }

    const currentCarouselIndex = carouselImages.findIndex(
      (img) => this.images.indexOf(img) === this._activeImageIndex,
    );

    let newCarouselIndex;

    if (direction > 0) {
      newCarouselIndex = (currentCarouselIndex + 1) % carouselImages.length;
    } else {
      newCarouselIndex = currentCarouselIndex - 1;

      if (newCarouselIndex < 0) {
        newCarouselIndex = carouselImages.length - 1;
      }
    }

    this._activeImageIndex = this.images.indexOf(
      carouselImages[newCarouselIndex],
    );
    this.updateCarousel();
  }

  updateCarousel() {
    const carouselImages = this.querySelectorAll(
      '.ProductItem__Image--carousel',
    );

    // Remove active class from all carousel images
    carouselImages.forEach((img) => img.classList.remove('active'));

    const activeImage = this.images[this._activeImageIndex];

    if (activeImage?.classList.contains('ProductItem__Image--carousel')) {
      activeImage.classList.add('active');
    }
  }

  showArrows() {
    this.toggleArrows('1');
  }

  hideArrows() {
    this.toggleArrows('0');
  }

  toggleArrows(opacity) {
    const arrows = this.querySelectorAll(
      '.ProductItem__PrevArrow, .ProductItem__NextArrow',
    );

    arrows.forEach((arrow) => (arrow.style.opacity = opacity));
  }

  createProgressBarAfterImageWrapper() {
    if (this.images.length <= 1) return;

    const progressBar = document.createElement('div');
    progressBar.className = 'ProductItem__ProgressBar';

    const progressLine = document.createElement('div');
    progressLine.className = 'ProductItem__ProgressLine';

    const progressFill = document.createElement('div');
    progressFill.className = 'ProductItem__ProgressFill';
    progressFill.style.width = `${100 / this.images.length}%`;

    progressLine.appendChild(progressFill);
    progressBar.appendChild(progressLine);

    const imageWrapper = this.querySelector('.ProductItem__ImageWrapper');

    if (imageWrapper && imageWrapper.parentNode) {
      imageWrapper.parentNode.insertBefore(
        progressBar,
        imageWrapper.nextSibling,
      );
    } else {
      this.appendChild(progressBar);
    }
  }

  handleMobileScroll() {
    const scrollWrapper = this.querySelector('.ProductItem__ScrollWrapper');

    if (!scrollWrapper) return;

    const scrollLeft = scrollWrapper.scrollLeft;
    const imageWidth = scrollWrapper.scrollWidth / this.images.length;
    const currentIndex = Math.round(scrollLeft / imageWidth);

    if (currentIndex !== this._activeImageIndex) {
      this._activeImageIndex = currentIndex;
      this.updateProgressBar();
    }
  }

  updateProgressBar() {
    const progressFill = this.querySelector('.ProductItem__ProgressFill');

    if (progressFill) {
      const percentage =
        ((this._activeImageIndex + 1) / this.images.length) * 100;

      progressFill.style.width = `${percentage}%`;
    }
  }

  setupPageScrollReset() {
    if (this.pageScrollHandler) {
      window.removeEventListener('scroll', this.pageScrollHandler);
    }

    let scrollTimeout;

    this.pageScrollHandler = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => this.resetMobileCarousel(), 150);
    };

    window.addEventListener('scroll', this.pageScrollHandler, {
      passive: true,
    });
  }

  resetMobileCarousel() {
    if (!this.isMobile || this._activeImageIndex === 0) return;

    const scrollWrapper = this.querySelector('.ProductItem__ScrollWrapper');

    if (!scrollWrapper) return;

    scrollWrapper.scrollTo({ left: 0, behavior: 'smooth' });

    this._activeImageIndex = 0;
    this.updateProgressBar();
  }

  updateImages(imageUrls, rolloverImageNumber = null) {
    const wasHovered = this.isHovered;

    let container = this.querySelector(
      '.ProductItem__ImageContainer.AspectRatio',
    );

    if (!container) {
      container = this.querySelector('.AspectRatio');

      if (!container) {
        return;
      }
    }

    container.querySelectorAll('img').forEach((img) => img.remove());
    container
      .querySelectorAll('.ProductItem__PrevArrow, .ProductItem__NextArrow')
      .forEach((arrow) => arrow.remove());
    container
      .querySelectorAll('.ProductItem__ScrollWrapper')
      .forEach((wrapper) => wrapper.remove());
    this.querySelectorAll('.ProductItem__ProgressBar').forEach((progressBar) =>
      progressBar.remove(),
    );

    const validUrls = imageUrls.filter((url) => url);

    if (validUrls.length === 0) return;

    this.isMobile = this.isMobileView();

    if (this.isMobile) {
      const scrollWrapper = document.createElement('div');
      scrollWrapper.className = 'ProductItem__ScrollWrapper';

      // Mobile image ordering: main, alternate (by URL, only once), then all other unique images
      let orderedUrls = [];
      const addedUrls = new Set();

      if (validUrls.length > 0) {
        orderedUrls.push(validUrls[0]);
        addedUrls.add(validUrls[0]);
      }

      // Add alternate image (by URL, only once, not main)
      let alternateUrl = null;

      if (
        typeof rolloverImageNumber === 'number' &&
        rolloverImageNumber > 1 &&
        rolloverImageNumber <= validUrls.length
      ) {
        alternateUrl = validUrls[rolloverImageNumber - 1];
      } else if (validUrls.length > 1) {
        alternateUrl = validUrls[1];
      }

      if (alternateUrl && !addedUrls.has(alternateUrl)) {
        orderedUrls.push(alternateUrl);
        addedUrls.add(alternateUrl);
      }

      // Add all other unique images
      validUrls.forEach((url) => {
        if (!addedUrls.has(url)) {
          orderedUrls.push(url);
          addedUrls.add(url);
        }
      });

      orderedUrls.forEach((url) => {
        const slide = document.createElement('div');
        slide.className = 'ProductItem__ImageSlide';
        const img = document.createElement('img');
        img.className = 'ProductItem__Image Image--fadeIn';
        const correctUrl = url.replace('_{width}x.', '_800x.');
        img.alt = '';
        img.src = correctUrl;
        slide.appendChild(img);
        scrollWrapper.appendChild(slide);
      });

      container.appendChild(scrollWrapper);
      this.images = Array.from(scrollWrapper.querySelectorAll('img'));
      this.createProgressBarAfterImageWrapper();
      scrollWrapper.addEventListener(
        'scroll',
        this.handleMobileScroll.bind(this),
      );
      this.setupPageScrollReset();
      if (window.lazySizes) {
        window.lazySizes.autoSizer.updateElems(this.images);
      }
    } else {
      // Determine alternate image index for hover behavior
      let alternateIndex = null;

      if (
        rolloverImageNumber &&
        rolloverImageNumber > 1 &&
        rolloverImageNumber <= validUrls.length
      ) {
        alternateIndex = rolloverImageNumber - 1; // Use specified rollover image
      } else if (validUrls.length > 1) {
        alternateIndex = 1; // Fallback: use second image as alternate for hover
      }

      // Create alternate image for hover effect (if exists)
      if (alternateIndex !== null) {
        const alternateImg = document.createElement('img');

        alternateImg.className =
          'ProductItem__Image ProductItem__Image--alternate Image--lazyLoad Image--fadeIn lazyautosizes';
        alternateImg.setAttribute(
          'data-src',
          validUrls[alternateIndex].replace('_{width}x.', '_800x.'),
        );
        alternateImg.setAttribute(
          'data-widths',
          '[200,300,400,600,800,900,1000,1200]',
        );
        alternateImg.setAttribute('data-sizes', 'auto');
        alternateImg.alt = '';
        container.appendChild(alternateImg);
      }

      const mainImg = document.createElement('img');

      mainImg.className =
        'ProductItem__Image Image--lazyLoad Image--fadeIn lazyautosizes';
      mainImg.setAttribute(
        'data-src',
        validUrls[0].replace('_{width}x.', '_800x.'),
      );
      mainImg.setAttribute(
        'data-widths',
        '[200,300,400,600,800,900,1000,1200]',
      );
      mainImg.setAttribute('data-sizes', 'auto');
      mainImg.alt = '';
      container.appendChild(mainImg);

      // Create carousel images
      validUrls.forEach((url) => {
        const img = document.createElement('img');

        img.className =
          'ProductItem__Image ProductItem__Image--carousel Image--lazyLoad lazyautosizes';
        img.setAttribute('data-src', url.replace('_{width}x.', '_800x.'));
        img.setAttribute('data-widths', '[200,300,400,600,800,900,1000,1200]');
        img.setAttribute('data-sizes', 'auto');
        img.alt = '';
        container.appendChild(img);
      });
    }

    if (!this.isMobile) {
      this.init();

      if (wasHovered) {
        this.isHovered = true;

        const carouselImages = this.images.filter((img) =>
          img.classList.contains('ProductItem__Image--carousel'),
        );

        if (carouselImages.length > 0) {
          this.showArrows();
        }
      }
    }
  }
}

customElements.define('product-item', ProductItem);
